import React, { memo, useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { PropertyInstance, PropertyType, PropertyValueType } from '../types';
import { RendererReadOnlyContext } from '../../../contexts/RendererReadOnlyContext';
import { PropertyValueData } from '../hooks/usePropertyManager';
import { PropertiesSection, Property } from '../../renderer/RendererRightPanel/PropertiesSection';
import { PropertyInput } from './PropertyInput';
import { useT } from '../../../translation/src';

interface PropertyGroupProps {
  title?: React.ReactNode;
  instances: PropertyInstance[];
  /** All instances in the panel (for custom components that need sibling access) */
  allInstances?: PropertyInstance[];
  onPropertyChange: (identifier: string, value: PropertyValueData, objectIdentifier?: string) => void;
  className?: string;
  disabled?: boolean;
  showErrors?: boolean;
  projectId?: string;
  /** Optional control rendered on the right of the first section's header (e.g. a filter button) */
  headerAction?: React.ReactNode;
}

/**
 * Helper to extract group toggle instance and other instances from a group
 */
interface GroupToggleInfo {
  toggleInstance: PropertyInstance | null;
  otherInstances: PropertyInstance[];
  isEnabled: boolean;
}

const getGroupToggleInfo = (instances: PropertyInstance[]): GroupToggleInfo => {
  const toggleInstance = instances.find((instance) => instance.config.ui?.isGroupToggle && instance.config.valueType === PropertyValueType.BOOLEAN) || null;

  const otherInstances = instances.filter((instance) => instance !== toggleInstance);

  // If there's a toggle, check its value; otherwise default to enabled
  const isEnabled = toggleInstance ? Boolean(toggleInstance.value) : true;

  return { toggleInstance, otherInstances, isEnabled };
};

/**
 * Component for rendering a group of parameters with consistent styling
 */
const PropertyGroup_: React.FC<PropertyGroupProps> = ({ title, instances, allInstances, onPropertyChange, className, disabled = false, showErrors = true, projectId, headerAction }) => {
  // Group instances by their group configuration
  const groupedInstances = useMemo(() => {
    const groups: Record<string, PropertyInstance[]> = {};

    instances.forEach((instance) => {
      const groupKey = instance.config.ui?.group || 'default';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(instance);
    });

    // Sort instances within each group by order
    Object.keys(groups).forEach((groupKey) => {
      groups[groupKey].sort((a, b) => {
        const orderA = a.config.ui?.order ?? 999;
        const orderB = b.config.ui?.order ?? 999;
        return orderA - orderB;
      });
    });

    return groups;
  }, [instances]);

  // Order groups relative to each other. A group's order is the smallest
  // groupOrder declared by any of its properties (default 999). The 'default'
  // group falls back to insertion order via a stable index tiebreak.
  const orderedGroupKeys = useMemo(() => {
    const keys = Object.keys(groupedInstances);
    const groupOrderFor = (groupKey: string) => Math.min(...groupedInstances[groupKey].map((instance) => instance.config.ui?.groupOrder ?? 999));
    return keys
      .map((groupKey, index) => ({ groupKey, index, order: groupOrderFor(groupKey) }))
      .sort((a, b) => a.order - b.order || a.index - b.index)
      .map((entry) => entry.groupKey);
  }, [groupedInstances]);

  const handlePropertyChange = useCallback(
    (instance: PropertyInstance, value: PropertyValueData) => {
      onPropertyChange(instance.config.identifier, value, instance.objectIdentifier);
    },
    [onPropertyChange],
  );

  // If there's only one group and it's the default, render without grouping
  const isDefaultGroupOnly = orderedGroupKeys.length === 1 && orderedGroupKeys[0] === 'default';

  if (isDefaultGroupOnly) {
    const { toggleInstance, otherInstances, isEnabled } = getGroupToggleInfo(groupedInstances.default);

    return (
      <div className={classNames('flex flex-col space-y-4 divide-y divide-gray-800', className)}>
        <PropertiesSection
          title={title}
          headerAction={headerAction}
          hasToggle={!!toggleInstance}
          toggleEnabled={isEnabled}
          onToggle={toggleInstance ? () => handlePropertyChange(toggleInstance, !toggleInstance.value) : undefined}
          toggleTitle={toggleInstance?.config.ui?.tooltip || toggleInstance?.config.name}
        >
          {otherInstances.map((instance) => (
            <PropertyDefinition
              key={`${instance.config.identifier}-${instance.objectIdentifier || 'user'}`}
              instance={instance}
              allInstances={allInstances}
              onChange={(value) => handlePropertyChange(instance, value)}
              disabled={disabled || !isEnabled}
              showError={showErrors}
              projectId={projectId}
            />
          ))}
        </PropertiesSection>
      </div>
    );
  }

  // Render with sub-groups
  return (
    <div className={classNames('flex flex-col space-y-4 divide-y divide-gray-800', className)}>
      {title && <div className="font-bold text-sm dark:text-white">{title}</div>}
      {orderedGroupKeys.map((groupKey, index) => (
        <TranslatedGroupSection
          key={groupKey}
          groupKey={groupKey}
          instances={groupedInstances[groupKey]}
          allInstances={allInstances}
          onPropertyChange={handlePropertyChange}
          disabled={disabled}
          showErrors={showErrors}
          projectId={projectId}
          headerAction={index === 0 ? headerAction : undefined}
        />
      ))}
    </div>
  );
};

/**
 * Sub-group section that translates the group name via useT
 */
const TranslatedGroupSection: React.FC<{
  groupKey: string;
  instances: PropertyInstance[];
  allInstances?: PropertyInstance[];
  onPropertyChange: (instance: PropertyInstance, value: PropertyValueData) => void;
  disabled: boolean;
  showErrors: boolean;
  projectId?: string;
  headerAction?: React.ReactNode;
}> = ({ groupKey, instances, allInstances, onPropertyChange, disabled, showErrors, projectId, headerAction }) => {
  const translatedGroupName = useT(groupKey, { swc: true });
  const { toggleInstance, otherInstances, isEnabled } = getGroupToggleInfo(instances);
  const translatedToggleTooltip = useT(toggleInstance?.config.ui?.tooltip || toggleInstance?.config.name || '', {
    swc: true,
  });

  return (
    <PropertiesSection
      title={groupKey !== 'default' ? translatedGroupName || groupKey : undefined}
      headerAction={headerAction}
      hasToggle={!!toggleInstance}
      toggleEnabled={isEnabled}
      onToggle={toggleInstance ? () => onPropertyChange(toggleInstance, !toggleInstance.value) : undefined}
      toggleTitle={translatedToggleTooltip}
    >
      {otherInstances.map((instance) => (
        <PropertyDefinition
          key={`${instance.config.identifier}-${instance.objectIdentifier || 'user'}`}
          instance={instance}
          allInstances={allInstances}
          onChange={(value) => onPropertyChange(instance, value)}
          disabled={disabled || !isEnabled}
          showError={showErrors}
          projectId={projectId}
        />
      ))}
    </PropertiesSection>
  );
};

interface PropertyDefinitionProps {
  instance: PropertyInstance;
  allInstances?: PropertyInstance[];
  onChange: (value: PropertyValueData) => void;
  disabled?: boolean;
  showError?: boolean;
  projectId?: string;
}

/**
 * Individual parameter property component
 */
/** Property scopes a read-only viewer may still change: they only affect that viewer's own session. */
const VIEWER_OWNED_PROPERTY_TYPES: ReadonlySet<PropertyType> = new Set([PropertyType.PROJECT_USER, PropertyType.USER_SETTINGS]);

const PropertyDefinition_: React.FC<PropertyDefinitionProps> = ({ instance, allInstances, onChange, disabled: disabledProp = false, showError = true, projectId }) => {
  const { config, error } = instance;
  // Read-only viewers can adjust their own view (visibility, filters) but not the project's shared state.
  const isReadOnly = useContext(RendererReadOnlyContext);
  const disabled = disabledProp || (isReadOnly && !VIEWER_OWNED_PROPERTY_TYPES.has(config.type));

  // Translate name, tooltip, and description at the rendering layer
  const translatedName = useT(config.name, { swc: true });
  const translatedTooltip = useT(config.ui?.tooltip || '', { swc: true });
  const translatedDescription = useT(config.description || '', { swc: true });

  // Generate property title with localization
  const propertyTitle = useMemo(() => {
    if (config.ui?.hideLabel) return null;

    // Prefer custom label override, then translated name
    return config.ui?.label || translatedName || config.name;
  }, [config.ui?.hideLabel, config.ui?.label, config.name, translatedName]);

  // Add tooltip if available
  const titleWithTooltip = useMemo(() => {
    const tooltip = translatedTooltip || translatedDescription;
    if (!tooltip) return propertyTitle;

    return (
      <span title={tooltip}>
        {propertyTitle}
        {config.ui?.icon && <config.ui.icon />}
      </span>
    );
  }, [propertyTitle, translatedTooltip, translatedDescription, config]);

  // Render custom component if provided
  if (config.ui?.customComponent) {
    const CustomComponent = config.ui.customComponent;
    return (
      <Property
        title={titleWithTooltip}
        className={classNames({
          'opacity-75': disabled || config.disabled,
        })}
      >
        <div className="flex flex-col w-full">
          <CustomComponent instance={instance} onChange={onChange} disabled={disabled} allInstances={allInstances} projectId={projectId} />
          {showError && error && <div className="mt-1 text-xs text-error-red-500">{error}</div>}
        </div>
      </Property>
    );
  }

  // Render read-only display
  if (config.readOnly) {
    const displayValue = config.formatValue ? config.formatValue(instance.value) : String(instance.value ?? '');
    return (
      <Property
        title={titleWithTooltip}
        className={classNames({
          'opacity-75': disabled || config.disabled,
        })}
      >
        <span className="text-sm dark:text-gray-300">{displayValue}</span>
      </Property>
    );
  }

  return (
    <Property
      title={titleWithTooltip}
      className={classNames({
        'opacity-75': disabled || config.disabled,
      })}
    >
      <div className="flex flex-col w-full">
        <PropertyInput instance={instance} onChange={onChange} disabled={disabled} />
        {showError && error && <div className="mt-1 text-xs text-error-red-500">{error}</div>}
      </div>
    </Property>
  );
};

export const PropertyDefinition = memo(PropertyDefinition_);

export const PropertyGroup = memo(PropertyGroup_);
